import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'

/**
 * Prefetch frequently-needed master data after login.
 * These are small, rarely-changing datasets that most pages need.
 *
 * IMPORTANT: prefetch ``queryFn`` MUST match the shape that hooks
 * sharing the same ``queryKey`` expect. ``/api/specialties/list/`` is
 * paginated (returns ``{count, next, previous, results}``); the
 * canonical ``useSpecialtyList`` hook unwraps to a bare array — the
 * prefetch must do the same, otherwise the cached object pollutes
 * the cache and consumers calling ``.find`` / ``.map`` on the array
 * shape crash (Sprint S hotfix #3 — discovered when
 * ExaminationTemplates.tsx was the first .find-caller).
 */
function unwrapList<T>(data: T[] | { results?: T[] }): T[] {
  return Array.isArray(data) ? data : (data?.results ?? [])
}

export function usePrefetchOnLogin() {
  const qc = useQueryClient()

  const prefetch = useCallback(() => {
    // Specialties list — needed for PatientDetail tabs + Settings >
    // Examination Templates. Unwraps to match useSpecialtyList().
    qc.prefetchQuery({
      queryKey: ['specialties', 'list'],
      queryFn: () => apiFetch<unknown>('/api/specialties/list/').then((d) => unwrapList(d as Parameters<typeof unwrapList>[0])),
      staleTime: 10 * 60_000,
    })

    // Tenant specialty config
    qc.prefetchQuery({
      queryKey: ['specialties', 'tenant-specialty'],
      queryFn: () => apiFetch('/api/specialties/tenant-specialty/'),
      staleTime: 5 * 60_000,
    })

    // Feature flags — needed for sidebar filtering
    qc.prefetchQuery({
      queryKey: ['feature-flags'],
      queryFn: () => apiFetch('/api/feature-flags/'),
      staleTime: 5 * 60_000,
    })
  }, [qc])

  return { prefetch }
}
